<template>
  <rest-form-page
    :page-title="pageTitle"
    :fetch-endpoint="fetchEndpoint"
    :save-endpoint="{}"
    :model="formModel"
    :nosave="true"
    :readonly="false"
  >
    <template slot="form" v-if="formModel">
      <el-dialog
        v-if="formModel"
        :title="$t('AppImpressions_PublicKey_Popup_Title')"
        :visible="!!showPublicKey"
        :close-on-click-modal="false"
        :before-close="closeKeyDialog"
        :modal="false"
        :modal-append-to-body="false"
      >
        <el-input
          :readonly="true"
          type="textarea"
          class="popup-textarea"
          v-model="showPublicKey"
          v-if="showPublicKey"
        />
      </el-dialog>
      <el-dialog
        v-if="updateKeyRow"
        :title="$t('AppImpressions_PublicKey_Popup_Title')"
        :visible="!!updateKeyRow"
        :close-on-click-modal="false"
        :before-close="closeKeyDialog"
        :modal="false"
        :modal-append-to-body="false"
      >
        <el-input
          type="textarea"
          spellcheck="false"
          class="popup-textarea"
          v-model="addPublicKey"
        />
        <span slot="footer" class="dialog-footer">
          <el-button type="primary" plain @click="closeKeyDialog">
            <span>{{ $t('Button_Cancel') }}</span>
          </el-button>
          <el-button
            type="primary"
            @click="updatePublicKey"
            :disabled="!addPublicKey"
          >
            <span>{{ $t('ApplicationList_Button_Add') }}</span>
            <i class="el-icon-arrow-right el-icon--save" />
          </el-button>
        </span>
      </el-dialog>
      <form-section
        :title="$t('AppImpressions_PublicKeys_Section_Title')"
        v-if="$can('manageProjectTechnical')"
      >
        <template slot="controls">
          <el-button
            slot="actions"
            type="primary"
            icon="el-icon-circle-plus"
            @click="addRow"
          >
            {{ $t('ApiIntegration_Button_AddPublicKey') }}
          </el-button>
        </template>

        <el-row>
          <el-col>
            <rest-list-editable
              :payload="fetchTokenEndpoint"
              :add-endpoint="addTokenEndpoint"
              :rules="rules"
              ref="rest-list"
              @dataLoaded="dataLoaded"
            >
              <rest-list-column
                prop="name"
                :title="$t('AppImpressions_PublicKeys_Column_Name')"
                :editable="true"
              >
                <template slot="input-edit" slot-scope="{ model }">
                  <el-input
                    :placeholder="$t('AppImpressions_PublicKeys_Column_Name')"
                    v-model="model.name"
                  />
                </template>
              </rest-list-column>
              <rest-list-column
                prop="client_id"
                :title="$t('AppImpressions_PublicKeys_Column_ClientID')"
                :editable="true"
              >
                <template slot="input-edit">
                  <div></div>
                </template>
              </rest-list-column>
              <rest-list-column
                prop="public_key"
                :title="$t('AppImpressions_PublicKeys_Column_PublicKey')"
                :filterable="false"
                :filter-input="false"
                :sortable="false"
                :editable="true"
              >
                <template slot-scope="scope">
                  <el-button
                    v-if="scope.row.public_key"
                    size="mini"
                    @click="showKey(scope)"
                    >{{ $t('AppImpressions_PublicKey_ShowBtn') }}
                    <el-icon name="view" />
                  </el-button>
                  <el-button v-else size="mini" @click="addKey(scope)"
                    >{{ $t('AppImpressions_PublicKey_AddBtn') }}
                    <el-icon name="circle-plus" />
                  </el-button>
                </template>
                <template slot="input-edit">
                  <div></div>
                </template>
              </rest-list-column>

              <rest-list-column-actions :editable="true">
                <template slot-scope="scope">
                  <rest-list-action-delete
                    :endpoint="rowDeleteEndpoint(scope.row)"
                    :name="scope.row.name"
                  />
                </template>
              </rest-list-column-actions>
            </rest-list-editable>
          </el-col>
        </el-row>
      </form-section>
      <form-section>
        <el-row>
          <el-col :span="12">
            <rest-form-item
              prop="endpoint"
              :label="$t('ApiIntegration_Field_Endpoint')"
            >
              <el-input v-model="apiEndpoint" disabled />
            </rest-form-item>
          </el-col>

          <el-col :span="12">
            <rest-form-item
              prop="apptoken"
              :label="$t('ApiIntegration_Field_AppToken')"
            >
              <el-input
                v-model="appToken"
                disabled
                :class="{ blurred: !showToken }"
              />
              <icon
                v-if="!showToken"
                glyph="eye"
                size="20"
                @click="showToken = true"
                :alt="$t('ApiIntegration_Button_AppTokenReveal')"
                :title="$t('ApiIntegration_Button_AppTokenReveal')"
              />
              <icon
                v-else
                glyph="eye-crossed"
                size="20"
                @click="showToken = false"
                :alt="$t('ApiIntegration_Button_AppTokenHide')"
                :title="$t('ApiIntegration_Button_AppTokenHide')"
              />
            </rest-form-item>
          </el-col>
        </el-row>
      </form-section>
    </template>
  </rest-form-page>
</template>

<script>
import RestFormPage from '@/common/components/rest/RestFormPage'
import RestFormItem from '@/common/components/rest/RestFormItem'
import FormSection from '@/common/components/form/FormSection'
import RestListEditable from '@/common/components/rest/RestListEditable'
import { projectEndpoint } from '@/core/helpers'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestApi from '@/core/services/RestApi'
import _ from 'lodash'

export default {
  name: 'ApiIntegrationPage',

  components: {
    RestListColumnActions,
    RestListActionDelete,
    RestListColumn,
    RestListEditable,
    RestFormPage,
    RestFormItem,
    FormSection
  },

  data() {
    return {
      updateKey: 0,
      formModel: null,
      showToken: false,
      showPublicKey: null,
      updateKeyRow: null,
      addPublicKey: null
    }
  },

  computed: {
    rules() {
      return {
        name: { required: true }
      }
    },

    fetchTokenEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `oauth-keys`,
        params: {
          sort: 'id',
          'per-page': 5
        }
      })
    },

    addTokenEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `oauth-keys`
      })
    },

    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    fetchEndpoint() {
      return {
        method: 'get',
        url: `projects/${this.$route.query.project_id}?expand=apptoken`,
        handler: data => {
          this.formModel = data
        }
      }
    },

    apiEndpoint() {
      return 'https://' + this.$store.state.app.domains.external_api
    },

    appToken: {
      get() {
        if (this.showToken) {
          return this.formModel.apptoken
        } else {
          return '•'.repeat(this.formModel.apptoken.length)
        }
      }
    }
  },
  methods: {
    showKey({ row }) {
      this.showPublicKey = row.public_key
    },
    addKey({ row }) {
      this.updateKeyRow = row
    },
    closeKeyDialog() {
      this.showPublicKey = false
      this.updateKeyRow = false
      this.addPublicKey = null
    },
    updatePublicKey() {
      let row = _.clone(this.updateKeyRow)
      row.public_key = this.addPublicKey
      RestApi(
        projectEndpoint({
          method: 'patch',
          url: `oauth-keys/${row.id}`,
          data: row
        })
      )
        .then(({ data }) => {
          this.updateKeyRow.public_key = this.addPublicKey
          this.closeKeyDialog()
          this.$message({
            message: this.$t('AppImpressions_PublicKey_Success_Msg'),
            type: 'success'
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    dataLoaded() {
      this.updateKey++
    },
    addRow() {
      const newRow = {
        client_id: '',
        public_key: '',
        newRow: true
      }
      this.$refs['rest-list'].addRow(newRow)
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `oauth-keys/${row.id}`
      })
    }
  }
}
</script>

<style scoped>
.el-form-item__content .icon {
  position: absolute;
  padding: 10px;
  top: 0;
  right: 0;
}

.el-form-item__content .blurred {
  -webkit-filter: blur(2px);
  -moz-filter: blur(2px);
  -ms-filter: blur(2px);
  -o-filter: blur(2px);
  filter: blur(2px);
}

>>> .popup-textarea textarea {
  height: 300px;
  resize: none;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/pages/ApiIntegrationPage.vue